import { Link } from 'react-router-dom';
import { Trans } from 'react-i18next';
import { Checkbox } from '../ui/checkbox';
import { Label } from '../ui/label';

interface GuardianDeclarationProps {
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  /**
   * The child it is about, when there is one to name. Left out — the guided
   * sign-up, where several children have just been typed in — the words speak
   * of the athletes above instead.
   */
  name?: string | null;
  /** Distinct per form: three of these can be on one page. */
  id?: string;
}

/**
 * The consent, in the guardian's own words: I hold parental responsibility, I
 * consent to an account being opened on this child's behalf, I have read the
 * policy section on managed accounts.
 *
 * This is the art. 8 GDPR consent — the one thing the whole guardianship flow
 * exists to collect — so it is always a deliberate tick, and nothing is sent
 * until it is given. The same words wherever a ward is opened (the letter's
 * page, the entry picker, the guided sign-up), because the server records the
 * same thing against the same policy version each time.
 */
export default function GuardianDeclaration({
  checked,
  onCheckedChange,
  name = null,
  id = 'guardian-declaration',
}: GuardianDeclarationProps) {
  const policyLink = (
    <Link
      to="/privacy-policy"
      target="_blank"
      rel="noopener noreferrer"
      className="text-primary underline underline-offset-2"
    />
  );

  return (
    <div className="flex items-start gap-3">
      <Checkbox
        id={id}
        checked={checked}
        onCheckedChange={(value) => onCheckedChange(value === true)}
        className="mt-0.5"
      />
      <Label htmlFor={id} className="text-sm font-normal leading-snug">
        {name ? (
          <Trans i18nKey="guardian.accept.declaration" values={{ name }} components={{ policy: policyLink }}>
            I hold parental responsibility for this child; I consent to an account being opened on their
            behalf; I have read the{' '}
            <Link
              to="/privacy-policy"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary underline underline-offset-2"
            >
              privacy policy section on managed accounts
            </Link>
            .
          </Trans>
        ) : (
          <Trans i18nKey="guardian.declarationForAthletes" components={{ policy: policyLink }}>
            I hold parental responsibility for the athletes above; I consent to an account being opened for
            each of them; I have read the{' '}
            <Link
              to="/privacy-policy"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary underline underline-offset-2"
            >
              privacy policy section on managed accounts
            </Link>
            .
          </Trans>
        )}
      </Label>
    </div>
  );
}
